export const LAST_LOGIN_USER_STORAGE_KEY = 'ld_last_login_user';

export type LastLoginMethod = 'email' | 'google' | 'sso';

const VALID_LOGIN_METHODS: LastLoginMethod[] = ['email', 'google', 'sso'];

const parseLoginMethod = (value: unknown): LastLoginMethod | undefined => {
    if (typeof value === 'string' && VALID_LOGIN_METHODS.includes(value as LastLoginMethod)) {
        return value as LastLoginMethod;
    }
    return undefined;
};

export interface ILastLoginUser {
    email: string;
    firstName?: string;
    lastName?: string;
    loginMethod?: LastLoginMethod;
}

export const getLastLoginUser = (): ILastLoginUser | null => {
    try {
        const raw = localStorage.getItem(LAST_LOGIN_USER_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.email) return null;
        return {
            email: parsed.email,
            firstName: parsed.firstName,
            lastName: parsed.lastName,
            loginMethod: parseLoginMethod(parsed.loginMethod),
        };
    } catch {
        return null;
    }
};

export const getRememberedLoginMethod = (user: ILastLoginUser | null): LastLoginMethod =>
    user?.loginMethod ?? 'email';

export const hasRememberedLogin = (): boolean => Boolean(getLastLoginUser()?.email);

export const getLastLoginUserDisplayName = (user: ILastLoginUser | null): string => {
    if (!user) return '';
    return [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
};

export const saveLastLoginUser = (
    user: {
        email?: string;
        firstName?: string;
        lastName?: string;
    },
    loginMethod?: LastLoginMethod,
) => {
    const email = user?.email?.trim();
    if (!email) return;
    const existing = getLastLoginUser();
    try {
        localStorage.setItem(
            LAST_LOGIN_USER_STORAGE_KEY,
            JSON.stringify({
                email,
                firstName: user.firstName ?? existing?.firstName,
                lastName: user.lastName ?? existing?.lastName,
                loginMethod: loginMethod ?? existing?.loginMethod,
            }),
        );
    } catch {
        // ignore
    }
};

export const clearLastLoginUser = () => {
    try {
        localStorage.removeItem(LAST_LOGIN_USER_STORAGE_KEY);
    } catch {
        // ignore
    }
};
